import React, { useState } from 'react';
import RtlStyles from 'i18n/rtl';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import { KompisInput } from '@inter-ikea-kompis/react-components';
import { getQuery, getRange } from 'utils/queryUtils';
import Modal, { ModalFooter, ModalHeader, Prompt } from '@ingka/modal';
import SkapaCheckbox from 'ui/Checkbox/SkapaCheckbox';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import styled from 'styled-components';
import Body from 'ui/Typography/Body';
import { SkapaTheme } from '@inter-ikea-kompis/themes';

const generateInteriorAssembly = getQuery().get('generateInteriorAssembly') === 'true';

const CheckboxWrapper = styled.div`
  > span {
    margin-top: 5px;
  }
`;

function GenerateAssemblyModal({
  visible,
  onGenerate,
  onGenerateAll,
  onCancel,
  onInput,
  assemblyId
}: {
  visible: boolean;
  onGenerate: (applicableFrameTypes?: string[], imagePrefix?: string) => void;
  onGenerateAll: (applicableFrameTypes?: string[], imagePrefix?: string) => void;
  onCancel: () => void;
  onInput: (value: string) => void;
  assemblyId: string;
}) {
  const handleInput = (e: any) => {
    onInput(e.detail.value);
  };

  const [hingedChecked, setHingedChecked] = useState(false);
  const [nonHingedChecked, setNonHingedChecked] = useState(false);
  const [cornerRegularFrameChecked, setCornerRegularFrameChecked] = useState(false);
  const [imagePrefix, setimagePrefix] = useState('');

  const handleGenerate = () => {
    const applicableFrameTypes = [];

    if (hingedChecked) applicableFrameTypes.push('hinged');
    if (nonHingedChecked) applicableFrameTypes.push('non-hinged');
    if (cornerRegularFrameChecked) applicableFrameTypes.push('cornerRegularFrame');

    onGenerate(applicableFrameTypes, imagePrefix);
  };

  const handleGenerateAll = () => {
    const applicableFrameTypes = [];

    if (hingedChecked) applicableFrameTypes.push('hinged');
    if (nonHingedChecked) applicableFrameTypes.push('non-hinged');
    if (cornerRegularFrameChecked) applicableFrameTypes.push('cornerRegularFrame');

    onGenerateAll(applicableFrameTypes, imagePrefix);
  };

  return (
    <Modal visible={visible} escapable={true} handleCloseBtn={onCancel}>
      <Prompt
        aria-labelledby="GeneratePrompt"
        title="Generate"
        titleId="GeneratePrompt"
        header={<ModalHeader ariaCloseTxt="Close prompt" />}
        footer={
          <ModalFooter>
            <Button
              text={'Generate'}
              onClick={handleGenerate}
              disabled={assemblyId.length === 0}
              type={ButtonTypeEnum.emphasised}
              mr={8}
            ></Button>
            {generateInteriorAssembly && (
              <Button
                text={'Generate all'}
                onClick={handleGenerateAll}
                disabled={assemblyId.length === 0}
                type={ButtonTypeEnum.emphasised}
                mr={8}
              ></Button>
            )}
            <Button text={'Cancel'} onClick={onCancel} type={ButtonTypeEnum.secondary}></Button>
          </ModalFooter>
        }
      >
        <RtlStyles>
          <>
            <KompisInput
              theme={SkapaTheme}
              onInput={handleInput}
              value={assemblyId}
              label="Enter assembly ID"
            />

            {getRange() === 'pax' && (
              <CheckboxWrapper>
                <Title type={ThemeFontStyleTypeEnum.headingSmall}>Applicable frame types</Title>
                <Body type={ThemeFontStyleTypeEnum.bodySmall}>
                  Select what type of frame the suggestion should be applied to
                </Body>
                <SkapaCheckbox
                  id={'1'}
                  label={'hinged'}
                  value={cornerRegularFrameChecked as any}
                  disabled={false}
                  checked={hingedChecked}
                  onChange={() => setHingedChecked(!hingedChecked)}
                  name={'hinged'}
                />
                <SkapaCheckbox
                  id={'2'}
                  label={'non-hinged'}
                  value={cornerRegularFrameChecked as any}
                  disabled={false}
                  checked={nonHingedChecked}
                  onChange={() => setNonHingedChecked(!nonHingedChecked)}
                  name={'non-hinged'}
                />
                <SkapaCheckbox
                  id={'3'}
                  label={'cornerRegularFrame'}
                  value={cornerRegularFrameChecked as any}
                  disabled={false}
                  checked={cornerRegularFrameChecked}
                  onChange={() => setCornerRegularFrameChecked(!cornerRegularFrameChecked)}
                  name={'cornerRegularFrame'}
                />
              </CheckboxWrapper>
            )}
            <KompisInput
              theme={SkapaTheme}
              onInput={e => setimagePrefix(e.detail.value)}
              value={imagePrefix}
              label="Image prefix (optional)"
            />
          </>
        </RtlStyles>
      </Prompt>
    </Modal>
  );
}

export default GenerateAssemblyModal;
